import { ConfigProvider, Flex, Typography } from 'antd';
import { useState } from 'react';
import { TFunction, useTranslation } from 'translations/hooks';

import { EditButton } from 'business/quality-control/pages/segment-history/components/segment-details-drawer/components/edit-button';
import ImagePointer from 'business/quality-control/pages/segment-history/components/segment-details-drawer/components/image-pointer';
import { UpdateDefectModal } from 'business/quality-control/pages/segment-history/components/segment-details-drawer/components/update-defect-modal';
import { UpdateRepairModal } from 'business/quality-control/pages/segment-history/components/segment-details-drawer/components/update-repair-modal';
import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import {
  QuarantineDefectOutputDto,
  RepairOutputDto,
} from 'generated/apiSchemas';
import { Quality_QuarantineDefectOrientationEnum_Enum } from 'generated/graphql';
import { formatDateWithMiddleDot } from 'technical/format/date';
import { Picture, PicturesList } from 'ui/pictures';
import segmentDrawDown from 'ui/quality-control/segment/down.png';
import segmentDrawUp from 'ui/quality-control/segment/up.png';

import styles from './index.module.scss';

const getDefectItems = (
  historyEntry: SegmentHistory['history'][number],
  t: TFunction,
) => [
  {
    label: t('qualityControl.segmentHistory.drawer.details.type', {
      context: 'defect',
    }),
    children: historyEntry.quarantineDefect?.type,
  },
  {
    label: t('qualityControl.segmentHistory.drawer.details.zone'),
    children: historyEntry.scanSite,
  },
  {
    label: t('qualityControl.segmentHistory.drawer.details.comment'),
    children: historyEntry.quarantineDefect?.description || '-',
  },
];

const getRepairItems = (
  historyEntry: SegmentHistory['history'][number],
  t: TFunction,
) => [
  {
    label: t('qualityControl.segmentHistory.drawer.details.type', {
      context: 'repair',
    }),
    children: historyEntry.quarantineDefect?.repair?.type,
  },
  {
    label: t('qualityControl.segmentHistory.drawer.details.comment'),
    children: historyEntry.quarantineDefect?.repair?.description || '-',
  },
];

export const Details = ({
  entryIndex,
  entryNumber,
  historyEntry,
  defect,
  pictures,
  isPicturesLoading,
  refetchData,
  mobile,
  isUserManager,
}: {
  entryIndex: number;
  entryNumber: number;
  historyEntry: SegmentHistory['history'][number];
  defect?: boolean;
  pictures: Picture[];
  isPicturesLoading: boolean;
  refetchData: () => void;
  mobile: boolean;
  isUserManager: boolean;
}) => {
  const { t } = useTranslation();
  const [currentDefect, setCurrentDefect] =
    useState<QuarantineDefectOutputDto | null>(null);
  const [currentRepair, setCurrentRepair] = useState<RepairOutputDto | null>(
    null,
  );

  const isDefectModalVisible = currentDefect !== null;
  const isRepairModalVisible = currentRepair !== null;

  const closeDefectModal = () => {
    setCurrentDefect(null);
  };
  const closeRepairModal = () => {
    setCurrentRepair(null);
  };

  const title = t('qualityControl.segmentHistory.drawer.details.title', {
    context: defect ? 'defect' : 'repair',
  });

  const date = defect
    ? historyEntry.quarantineDefect?.createdAt
    : historyEntry.quarantineDefect?.repair?.createdAt;

  const position = defect
    ? {
        x: historyEntry.quarantineDefect?.positionX ?? 0,
        y: historyEntry.quarantineDefect?.positionY ?? 0,
      }
    : { x: 0, y: 0 };

  let image = segmentDrawUp;
  if (
    defect &&
    historyEntry.quarantineDefect?.orientation ===
      Quality_QuarantineDefectOrientationEnum_Enum.Down
  ) {
    image = segmentDrawDown;
  }

  const items = defect
    ? getDefectItems(historyEntry, t)
    : getRepairItems(historyEntry, t);

  return (
    <Flex gap={16} vertical>
      <Flex justify="space-between">
        <div>
          <ConfigProvider
            theme={{
              components: {
                Typography: {
                  titleMarginBottom: 0,
                },
              },
            }}
          >
            <Typography.Title level={5} className={styles.entryTitle}>
              {title} {entryIndex + 1}/{entryNumber}
            </Typography.Title>
          </ConfigProvider>

          <span className={styles.detailLabel}>
            {formatDateWithMiddleDot(date)}
          </span>
        </div>

        {isUserManager ? (
          <EditButton
            historyEntry={historyEntry}
            isDefect={defect ?? false}
            setCurrentDefect={setCurrentDefect}
            setCurrentRepair={setCurrentRepair}
            mobile={mobile}
          />
        ) : null}
      </Flex>

      {defect ? <ImagePointer position={position} image={image} /> : null}

      {items.map((item) => (
        <div key={item.label}>
          <Flex vertical>
            <span className={styles.detailLabel}>{item.label}</span>
            <span className={styles.detailValue}>{item.children ?? '-'}</span>
          </Flex>
        </div>
      ))}

      <PicturesList pictures={pictures} isLoading={isPicturesLoading} />

      <UpdateDefectModal
        isModalVisible={isDefectModalVisible}
        closeModal={closeDefectModal}
        defect={currentDefect}
        zone={historyEntry.scanSiteId}
        refetchData={refetchData}
      />
      <UpdateRepairModal
        isModalVisible={isRepairModalVisible}
        closeModal={closeRepairModal}
        repair={currentRepair}
        refetchData={refetchData}
      />
    </Flex>
  );
};
